import React from 'react';
import NumericInput from './NumericInput';
import ColorPicker from './ColorPicker';
import { MA_TYPES, PRICE_SOURCES } from '../../utils/indicatorDefaults';

/**
 * IndicatorParametersForm Component
 *
 * Dynamic form for configuring indicator parameters based on indicator type
 *
 * @param {string} indicator - Indicator name (e.g., 'macd', 'rsi')
 * @param {object} settings - Current settings object
 * @param {function} onSettingsChange - Callback when any setting changes
 * @param {string} activeTab - Currently active tab ('parameters' or 'colors')
 */
function IndicatorParametersForm({ indicator, settings, onSettingsChange, activeTab }) {
  const updateSetting = (key, value) => {
    onSettingsChange({
      ...settings,
      [key]: value
    });
  };

  const updateColor = (colorKey, value) => {
    onSettingsChange({
      ...settings,
      colors: {
        ...settings.colors,
        [colorKey]: value
      }
    });
  };

  // Render parameters tab
  const renderParametersTab = () => {
    switch (indicator) {
      case 'macd':
        return (
          <div className="space-y-4">
            <NumericInput
              label="Fast Length"
              value={settings.fast || 12}
              onChange={(val) => updateSetting('fast', val)}
              min={1}
              max={200}
              description="Period for fast EMA (typically 12)"
            />

            <NumericInput
              label="Slow Length"
              value={settings.slow || 26}
              onChange={(val) => updateSetting('slow', val)}
              min={1}
              max={200}
              description="Period for slow EMA (typically 26)"
            />

            <NumericInput
              label="Signal Length"
              value={settings.signal || 9}
              onChange={(val) => updateSetting('signal', val)}
              min={1}
              max={50}
              description="Period for signal line (typically 9)"
            />

            <div className="space-y-1">
              <label className="block text-sm font-medium text-text">
                Moving Average Type
              </label>
              <select
                value={settings.ma_type || 'ema'}
                onChange={(e) => updateSetting('ma_type', e.target.value)}
                className="w-full px-3 py-2 bg-background border border-muted rounded text-text focus:outline-none focus:border-primary"
              >
                {MA_TYPES.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-1">
              <label className="block text-sm font-medium text-text">
                Price Source
              </label>
              <select
                value={settings.source || 'close'}
                onChange={(e) => updateSetting('source', e.target.value)}
                className="w-full px-3 py-2 bg-background border border-muted rounded text-text focus:outline-none focus:border-primary"
              >
                {PRICE_SOURCES.map((source) => (
                  <option key={source.value} value={source.value}>
                    {source.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        );

      case 'rsi':
        return (
          <div className="space-y-4">
            <NumericInput
              label="Length"
              value={settings.period || 14}
              onChange={(val) => updateSetting('period', val)}
              min={1}
              max={200}
              description="Period for RSI calculation (typically 14)"
            />

            <NumericInput
              label="Overbought Level"
              value={settings.overbought || 70}
              onChange={(val) => updateSetting('overbought', val)}
              min={50}
              max={100}
              description="Overbought threshold (typically 70)"
            />

            <NumericInput
              label="Oversold Level"
              value={settings.oversold || 30}
              onChange={(val) => updateSetting('oversold', val)}
              min={0}
              max={50}
              description="Oversold threshold (typically 30)"
            />

            <div className="space-y-1">
              <label className="block text-sm font-medium text-text">
                Price Source
              </label>
              <select
                value={settings.source || 'close'}
                onChange={(e) => updateSetting('source', e.target.value)}
                className="w-full px-3 py-2 bg-background border border-muted rounded text-text focus:outline-none focus:border-primary"
              >
                {PRICE_SOURCES.map((source) => (
                  <option key={source.value} value={source.value}>
                    {source.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        );

      case 'bollinger':
        return (
          <div className="space-y-4">
            <NumericInput
              label="Length"
              value={settings.period || 20}
              onChange={(val) => updateSetting('period', val)}
              min={1}
              max={200}
              description="Period for moving average (typically 20)"
            />

            <NumericInput
              label="Upper Deviations"
              value={settings.deviations_upper || 2.0}
              onChange={(val) => updateSetting('deviations_upper', val)}
              min={0.1}
              max={5.0}
              step={0.1}
              description="Standard deviations for upper band"
            />

            <NumericInput
              label="Lower Deviations"
              value={settings.deviations_lower || 2.0}
              onChange={(val) => updateSetting('deviations_lower', val)}
              min={0.1}
              max={5.0}
              step={0.1}
              description="Standard deviations for lower band"
            />

            <NumericInput
              label="Displace"
              value={settings.displace || 0}
              onChange={(val) => updateSetting('displace', val)}
              min={-50}
              max={50}
              description="Shift bands forward/backward (0 = no shift)"
            />

            <div className="space-y-1">
              <label className="block text-sm font-medium text-text">
                Moving Average Type
              </label>
              <select
                value={settings.ma_type || 'sma'}
                onChange={(e) => updateSetting('ma_type', e.target.value)}
                className="w-full px-3 py-2 bg-background border border-muted rounded text-text focus:outline-none focus:border-primary"
              >
                {MA_TYPES.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-1">
              <label className="block text-sm font-medium text-text">
                Price Source
              </label>
              <select
                value={settings.source || 'close'}
                onChange={(e) => updateSetting('source', e.target.value)}
                className="w-full px-3 py-2 bg-background border border-muted rounded text-text focus:outline-none focus:border-primary"
              >
                {PRICE_SOURCES.map((source) => (
                  <option key={source.value} value={source.value}>
                    {source.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        );

      case 'volume':
        return (
          <div className="space-y-4">
            <NumericInput
              label="MA Period"
              value={settings.ma_period || 20}
              onChange={(val) => updateSetting('ma_period', val)}
              min={1}
              max={200}
              description="Period for volume moving average"
            />

            <div className="space-y-1">
              <label className="block text-sm font-medium text-text">
                Moving Average Type
              </label>
              <select
                value={settings.ma_type || 'sma'}
                onChange={(e) => updateSetting('ma_type', e.target.value)}
                className="w-full px-3 py-2 bg-background border border-muted rounded text-text focus:outline-none focus:border-primary"
              >
                {MA_TYPES.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        );

      case 'sma_50':
      case 'sma_200':
      case 'sma_720':
        const defaultPeriod = indicator === 'sma_50' ? 50 : indicator === 'sma_200' ? 200 : 720;
        return (
          <div className="space-y-4">
            <NumericInput
              label="Length"
              value={settings.period || defaultPeriod}
              onChange={(val) => updateSetting('period', val)}
              min={1}
              max={1000}
              description="Period for SMA calculation"
            />

            <NumericInput
              label="Displace"
              value={settings.displace || 0}
              onChange={(val) => updateSetting('displace', val)}
              min={-50}
              max={50}
              description="Shift line forward/backward (0 = no shift)"
            />

            <div className="space-y-1">
              <label className="block text-sm font-medium text-text">
                Price Source
              </label>
              <select
                value={settings.source || 'close'}
                onChange={(e) => updateSetting('source', e.target.value)}
                className="w-full px-3 py-2 bg-background border border-muted rounded text-text focus:outline-none focus:border-primary"
              >
                {PRICE_SOURCES.map((source) => (
                  <option key={source.value} value={source.value}>
                    {source.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        );

      case 'ema':
        return (
          <div className="space-y-4">
            <NumericInput
              label="Length"
              value={settings.period || 12}
              onChange={(val) => updateSetting('period', val)}
              min={1}
              max={200}
              description="Period for EMA calculation"
            />

            <NumericInput
              label="Displace"
              value={settings.displace || 0}
              onChange={(val) => updateSetting('displace', val)}
              min={-50}
              max={50}
              description="Shift line forward/backward (0 = no shift)"
            />

            <div className="space-y-1">
              <label className="block text-sm font-medium text-text">
                Price Source
              </label>
              <select
                value={settings.source || 'close'}
                onChange={(e) => updateSetting('source', e.target.value)}
                className="w-full px-3 py-2 bg-background border border-muted rounded text-text focus:outline-none focus:border-primary"
              >
                {PRICE_SOURCES.map((source) => (
                  <option key={source.value} value={source.value}>
                    {source.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        );

      case 'vwap':
        return (
          <div className="text-center py-8 text-gray-400">
            <p>VWAP has no configurable parameters</p>
            <p className="text-sm mt-2">Volume Weighted Average Price is calculated automatically</p>
          </div>
        );

      case 'ac':
        return (
          <div className="text-center py-8 text-gray-400">
            <p>Acceleration/Deceleration has no configurable parameters</p>
            <p className="text-sm mt-2">AC is calculated using standard formulas</p>
          </div>
        );

      default:
        return (
          <div className="text-center py-8 text-gray-400">
            <p>No parameters available for this indicator</p>
          </div>
        );
    }
  };

  // Render colors tab
  const renderColorsTab = () => {
    switch (indicator) {
      case 'macd':
        return (
          <div className="space-y-4">
            <ColorPicker
              label="MACD Line Color"
              value={settings.colors?.macd || '#06b6d4'}
              onChange={(val) => updateColor('macd', val)}
            />
            <ColorPicker
              label="Signal Line Color"
              value={settings.colors?.signal || '#fb923c'}
              onChange={(val) => updateColor('signal', val)}
            />
            <ColorPicker
              label="Histogram Color"
              value={settings.colors?.histogram || '#6b7280'}
              onChange={(val) => updateColor('histogram', val)}
            />
          </div>
        );

      case 'rsi':
        return (
          <div className="space-y-4">
            <ColorPicker
              label="RSI Line Color"
              value={settings.colors?.rsi || '#a855f7'}
              onChange={(val) => updateColor('rsi', val)}
            />
            <ColorPicker
              label="Overbought Line Color"
              value={settings.colors?.overbought || '#ef4444'}
              onChange={(val) => updateColor('overbought', val)}
            />
            <ColorPicker
              label="Oversold Line Color"
              value={settings.colors?.oversold || '#10b981'}
              onChange={(val) => updateColor('oversold', val)}
            />
            <ColorPicker
              label="Fill Color"
              value={settings.colors?.fill || 'rgba(168, 85, 247, 0.1)'}
              onChange={(val) => updateColor('fill', val)}
              showAlpha={true}
            />
          </div>
        );

      case 'bollinger':
        return (
          <div className="space-y-4">
            <ColorPicker
              label="Upper Band Color"
              value={settings.colors?.upper || '#60a5fa'}
              onChange={(val) => updateColor('upper', val)}
            />
            <ColorPicker
              label="Middle Band Color"
              value={settings.colors?.middle || '#60a5fa'}
              onChange={(val) => updateColor('middle', val)}
            />
            <ColorPicker
              label="Lower Band Color"
              value={settings.colors?.lower || '#60a5fa'}
              onChange={(val) => updateColor('lower', val)}
            />
          </div>
        );

      case 'volume':
        return (
          <div className="space-y-4">
            <ColorPicker
              label="Volume Bars Color"
              value={settings.colors?.bars || '#6b7280'}
              onChange={(val) => updateColor('bars', val)}
            />
            <ColorPicker
              label="MA Line Color"
              value={settings.colors?.ma || '#3b82f6'}
              onChange={(val) => updateColor('ma', val)}
            />
          </div>
        );

      case 'sma_50':
      case 'sma_200':
      case 'sma_720':
      case 'ema':
        const defaultColor = indicator === 'sma_50' ? '#10b981' :
                            indicator === 'sma_200' ? '#ef4444' :
                            indicator === 'sma_720' ? '#f59e0b' : '#8b5cf6';
        return (
          <div className="space-y-4">
            <ColorPicker
              label="Line Color"
              value={settings.color || defaultColor}
              onChange={(val) => onSettingsChange({ ...settings, color: val })}
            />
          </div>
        );

      case 'vwap':
        return (
          <div className="space-y-4">
            <ColorPicker
              label="Line Color"
              value={settings.color || '#06b6d4'}
              onChange={(val) => onSettingsChange({ ...settings, color: val })}
            />
          </div>
        );

      case 'ac':
        return (
          <div className="space-y-4">
            <ColorPicker
              label="Bullish Color"
              value={settings.colors?.bullish || '#10b981'}
              onChange={(val) => updateColor('bullish', val)}
            />
            <ColorPicker
              label="Bearish Color"
              value={settings.colors?.bearish || '#ef4444'}
              onChange={(val) => updateColor('bearish', val)}
            />
          </div>
        );

      default:
        return (
          <div className="text-center py-8 text-gray-400">
            <p>No colors available for this indicator</p>
          </div>
        );
    }
  };

  // Render based on active tab
  if (activeTab === 'parameters') {
    return renderParametersTab();
  } else if (activeTab === 'colors') {
    return renderColorsTab();
  }

  return null;
}

export default IndicatorParametersForm;
